import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useTheme } from './ThemeProvider';
import { Button } from './ui/button';
import { ThemeToggle } from './ui/theme-toggle';
import { cn } from '../lib/utils';
import { Menu } from 'lucide-react';

// Add the animation styles for the button
const animatedButtonStyles = `
  @keyframes gradientAnimation {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  .animate-gradient {
    background-size: 200% 200%;
    animation: gradientAnimation 5s ease infinite;
  }
`;

export default function Layout({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { hasActiveSubscription, handleManageSubscription } = useSubscription();
  const location = useLocation();
  const { isDarkMode } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => setIsMenuOpen(prev => !prev);



  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container max-w-4xl mx-auto flex h-14 items-center justify-between">
          {/* Logo - hide on mobile */}
          <div className="mr-4 hidden md:flex">
            <Link to="/" className="flex items-center space-x-2">
              <img
                src="/HandshakeLogo.png"
                alt="Quick Cover Letter Logo"
                className="h-8 w-auto"
                style={{ borderRadius: '6px' }}
              />
            </Link>
          </div>
          
          {/* Mobile logo/hamburger menu */}
          <div className="flex items-center md:hidden">
            <button 
              className="p-2 rounded focus:outline-none focus:ring-2"
              onClick={toggleMenu}
            >
              <Menu size={24} />
            </button>
            <img
              src="/HandshakeLogo.png"
              alt="Quick Cover Letter Logo"
              className="h-6 w-auto ml-2"
              style={{ borderRadius: '5px' }}
            />
          </div>
          
          {user && (
            <>
              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center space-x-6 text-sm font-medium">
                <Link 
                  to="/dashboard" 
                  className={cn(
                    "transition-colors hover:text-foreground/80", 
                    location.pathname === "/dashboard" ? "text-foreground" : "text-foreground/60"
                  )}
                >
                  Dashboard
                </Link>
                <Link 
                  to="/profile" 
                  className={cn(
                    "transition-colors hover:text-foreground/80", 
                    location.pathname === "/profile" ? "text-foreground" : "text-foreground/60"
                  )}
                >
                  Profile
                </Link>
                <Link 
                  to="/account" 
                  className={cn(
                    "transition-colors hover:text-foreground/80", 
                    location.pathname === "/account" ? "text-foreground" : "text-foreground/60"
                  )}
                >
                  Account
                </Link>
              </nav>
              
              <div className="flex-1"></div>
              
              <div className="flex items-center">
                {/* Hide "Activate Chrome Extension" on mobile */}
                {!hasActiveSubscription && (
                  <Button 
                    variant="default" 
                    className="mr-4 hidden md:block bg-[#CAF84C] text-black hover:bg-[#b9e93a] border-none shadow-md hover:shadow-lg transition-all duration-200"
                    onClick={handleManageSubscription}
                  >
                    Activate Chrome Extension
                  </Button>
                )}
                
                <ThemeToggle />
              </div>
            </>
          )}
        </div>

        {/* Mobile Menu (shown only when isMenuOpen is true) */}
        {user && isMenuOpen && (
          <div className="md:hidden bg-background border-t px-4 py-2 space-y-2">
            {/* Navigation links */}
            <Link
              to="/dashboard"
              className="block text-sm py-2"
              onClick={() => setIsMenuOpen(false)}
            >
              Dashboard
            </Link>
            <Link
              to="/profile"
              className="block text-sm py-2"
              onClick={() => setIsMenuOpen(false)}
            >
              Profile
            </Link>
            <Link
              to="/account"
              className="block text-sm py-2"
              onClick={() => setIsMenuOpen(false)}
            >
              Account
            </Link>
          </div>
        )}
      </header>
      
      <main className="flex-1 container max-w-4xl mx-auto py-6">
        {children}
      </main>
      <style>{animatedButtonStyles}</style>
    </div>
  );
}
